import React from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Rating from "@mui/material/Rating";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import CardActions from "@mui/material/CardActions";
import Avatar from "@mui/material/Avatar";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import {useTheme} from "@mui/material/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import {useTranslation} from "react-i18next";
import AlertTitle from "@mui/material/AlertTitle";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {postData} from "../../utils/api";
// eslint-disable-next-line import/no-cycle
import {UserContext} from "../../App";
import ExperimentTypeSelector from "../experiment/ExperimentTypeSelector";
// get demo information (will be translated in the corresponding language)
import demo from "../../locales/de/demoDatasets";

const Alert = React.forwardRef((props, ref) => (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

export default function DemoDatasets() {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();
    const {fetchDatasets} = React.useContext(UserContext);
    const [datasetUploaded, setDatasetUploaded] = React.useState(false);

    // Error Snackbars
    const [snackbarSettings, setSnackbarSettings] = React.useState({
        isOpen: false,
        type: "info",
        message: "",
    });

    const handleDatasetSelection = (fileName) => () => {
        // send selection to backend to get dataset
        postData(`${process.env.REACT_APP_BACKEND_URL}/api/v0/demodataset`, {
            datasetName: fileName,
            delimiter: ",",
        }).then((data) => {
            const dictCopy = {...snackbarSettings};
            dictCopy.isOpen = true;

            if (data.status === 201) {
                dictCopy.type = "success";
                dictCopy.message = String(
                    t("datasetCreator.DatasetCreated_3") +
                    fileName +
                    t("datasetCreator.DatasetCreated_4")
                );
                // update datasets and set current dataset to just uploaded dataset
                fetchDatasets({latest: fileName});
                setDatasetUploaded(true);
            }
            if (data.status === 409) {
                dictCopy.type = "info";
                dictCopy.message = t(
                    "datasetConfigurator.ErrorDemoDatasetAlreadyExists"
                );
            }
            setSnackbarSettings(dictCopy);
        });
    };

    return (
        <>
            <Snackbar open={snackbarSettings.isOpen} autoHideDuration={8000}>
                <Alert severity={snackbarSettings.type}>
                    {snackbarSettings.message}
                </Alert>
            </Snackbar>

            <Grid
                id="examples-container"
                container
                spacing={4}
                sx={{padding: theme.spacing(4, 1)}}
            >
                {datasetUploaded ? (
                    <>
                        <Grid item xs={12}>
                            <Alert severity="success">
                                <AlertTitle>
                                    {t("datasetCreator.DatasetCreated_1")}{" "}
                                    {t("datasetCreator.DatasetCreated_2")}
                                </AlertTitle>
                            </Alert>
                        </Grid>
                        <Grid item xs={12}>
                            <ExperimentTypeSelector theme={theme}/>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h4" gutterBottom>
                                {" "}
                                {t("demo.PageTitle")}
                            </Typography>
                        </Grid>
                        {Object.keys(demo.examples).map((datasetName) => (
                            <Grid key={datasetName} item lg={5} xs={6}>
                                <Card sx={{maxWidth: 800}}>
                                    <CardMedia
                                        component="img"
                                        height="250"
                                        image={t(`demo.examples.${datasetName}.TeaserImageUrl`)}
                                        alt=" Check out our example experiments."
                                    />
                                    <CardContent sx={{minHeight: 550}}>
                                        <Typography variant="h5" gutterBottom>
                                            {t(`demo.examples.${datasetName}.Title`)}
                                        </Typography>
                                        <Typography component="legend">
                                            {t("demo.Difficulty")}
                                        </Typography>
                                        <Rating
                                            name="read-only"
                                            value={parseInt(
                                                t(`demo.examples.${datasetName}.DifficultyRating`),
                                                10
                                            )}
                                            readOnly
                                        />
                                        <Typography>
                                            {t(`demo.examples.${datasetName}.Description`)}
                                        </Typography>
                                        <List
                                            sx={{
                                                width: "100%",
                                                paddingTop: theme.spacing(4),
                                                maxWidth: 500,
                                                bgcolor: "background.paper",
                                            }}
                                        >
                                            <Typography variant="h7">
                                                {t("examples.DatasetInformation")}
                                            </Typography>
                                            <ListItem>
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        <ViewHeadlineIcon/>
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={t("demo.NumDatapoints")}
                                                    secondary={t(
                                                        `demo.examples.${datasetName}.NumDatapoints`
                                                    )}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        <ViewColumnIcon/>
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={t("demo.NumFeatures")}
                                                    secondary={t(`demo.examples.${datasetName}.Features`)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        <ScatterPlotIcon/>
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={t("demo.TargetDescription")}
                                                    secondary={t(`demo.examples.${datasetName}.Targets`)}
                                                />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                    <CardActions
                                        onClick={handleDatasetSelection(
                                            t(`demo.examples.${datasetName}.DatasetFileName`)
                                        )}
                                    >
                                        <Button size="small">
                                            {t("common.SelectButton")}
                                            <KeyboardArrowRightIcon/>
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </>
                )}
            </Grid>
        </>
    );
}
